import React from 'react';

// station-settings-admin: the settings the station's helpers declare
// (`@Helper(settings=[...])`), built from the hub's schema. Station settings are edited
// here and saved per helper into config.settings.<Helper> (secrets into
// config._private.settings.<Helper>, write-only). User settings are listed for
// reference: users set them from the station's menu.

const inputStyle = { padding: '4px 6px', fontSize: '0.85rem', border: '1px solid #cbd5e1', borderRadius: 4 };

const STATUS_NOTE = {
  invalid: 'The stored value isn’t valid; helpers use the default until it’s fixed.',
  missing: 'Required, and not set.',
};

// Top-level config keys a declared setting still reads its value from (`legacy_key`,
// and nothing stored in its own place yet): edit them in Settings, not Properties. Once
// the value has moved, the old key shows in Properties again so it can be cleared.
export function declaredLegacyKeys(schema) {
  const keys = new Set();
  for (const set of (schema && schema.sets) || []) {
    for (const s of set.settings || []) {
      if (!s.from_old_key) continue;
      const root = (s.legacy_key || '').split('.')[0];
      if (root && root !== '_private') keys.add(root);
    }
  }
  return [...keys];
}

// The config patch for a set of edits: {settings: {Set: {key: v}}, _private: {settings: ...}}.
// Exported for the tests.
export function settingsPatch(edits, schema) {
  const secretKeys = new Set();
  for (const set of (schema && schema.sets) || []) {
    for (const s of set.settings || []) if (s.secret) secretKeys.add(`${set.name}.${s.key}`);
  }
  const pub = {};
  const priv = {};
  for (const [id, value] of Object.entries(edits || {})) {
    const dot = id.indexOf('.');
    const set = id.slice(0, dot);
    const key = id.slice(dot + 1);
    const target = secretKeys.has(id) ? priv : pub;
    target[set] = { ...(target[set] || {}), [key]: value };
  }
  const patch = {};
  if (Object.keys(pub).length) patch.settings = pub;
  if (Object.keys(priv).length) patch._private = { settings: priv };
  return patch;
}

function StationInput({ setting, value, onChange }) {
  const label = setting.label;
  if (setting.secret) {
    return (
      <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <input aria-label={label} type="password" autoComplete="new-password" style={inputStyle}
               placeholder={setting.is_set ? '•••••••• (set — type to replace)' : 'Not set'}
               value={typeof value === 'string' ? value : ''}
               onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)} />
        {setting.is_set && (
          <button type="button" onClick={() => onChange(null)} style={{ fontSize: '0.8rem' }}>
            {value === null ? 'Will clear' : 'Clear'}
          </button>
        )}
      </span>
    );
  }
  if (setting.type === 'boolean') {
    // Nothing stored means helpers read the default, so that's what the box shows.
    const checked = value ?? setting.default ?? false;
    return (
      <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input aria-label={label} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
        {value == null && setting.default != null && (
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>(default: {setting.default ? 'on' : 'off'})</span>
        )}
      </span>
    );
  }
  if (setting.type === 'select') {
    return (
      <select aria-label={label} value={value ?? ''} style={inputStyle}
              onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}>
        <option value="">{setting.default != null ? `Default (${setting.default})` : '(not set)'}</option>
        {(setting.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (setting.type === 'map') {
    return <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Edited by the helper, not here.</span>;
  }
  return (
    <input aria-label={label} type={setting.type === 'number' ? 'number' : 'text'} style={inputStyle}
           placeholder={setting.default != null ? `Default: ${setting.default}` : ''}
           value={value ?? ''}
           onChange={(e) => {
             const raw = e.target.value;
             // A number the browser can't parse reads as '' too; ignore it rather than clear.
             if (raw === '' && e.target.validity && e.target.validity.badInput) return;
             if (raw === '') onChange(null);
             else onChange(setting.type === 'number' ? Number(raw) : raw);
           }} />
  );
}

// The station's settings schema from aveq-admin's proxy of the hub: `{sets}`, or throws.
export async function fetchSettingsSchema(stationId) {
  const r = await fetch(`/api/stations/${encodeURIComponent(stationId)}/settings-schema`,
    { credentials: 'same-origin' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const body = await r.json();
  return (body && body.entity) || { sets: [] };
}

// `schema` is null while loading and 'failed' when it couldn't be read.
export default function SettingsSection({ schema, edits, onEditsChange }) {
  if (schema === 'failed') return <p style={{ color: '#b91c1c', fontSize: '0.85rem' }}>Couldn’t load the station’s settings.</p>;
  if (!schema) return <p style={{ color: '#64748b', fontSize: '0.85rem' }}>Loading…</p>;
  if (!schema.sets.length) {
    return (
      <p style={{ color: '#475569', fontSize: '0.85rem' }}>
        No helper on this station declares settings yet. Values it reads without declaring
        them are under Properties.
      </p>
    );
  }

  const set = (id, value) => {
    const next = { ...(edits || {}) };
    if (value === undefined) delete next[id];
    else next[id] = value;
    onEditsChange(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {schema.sets.map((group) => {
        const station = group.settings.filter((s) => (s.scope || 'station') === 'station');
        const user = group.settings.filter((s) => s.scope === 'user');
        return (
          <fieldset key={group.name} style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 10 }}>
            <legend style={{ fontWeight: 600, fontSize: '0.85rem', padding: '0 4px' }}>{group.name}</legend>
            {station.map((s) => {
              const id = `${group.name}.${s.key}`;
              const value = id in (edits || {}) ? edits[id] : s.value;
              return (
                <div key={id} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) 2fr', gap: 8, alignItems: 'start', marginBottom: 8 }}>
                  <div style={{ fontSize: '0.85rem' }}>
                    {s.label}{s.required && <span title="Required" style={{ color: '#b91c1c' }}> *</span>}
                    {s.help && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{s.help}</div>}
                  </div>
                  <div>
                    <StationInput setting={s} value={value} onChange={(v) => set(id, v)} />
                    {STATUS_NOTE[s.status] && !(id in (edits || {})) && (
                      <div role="note" style={{ fontSize: '0.75rem', color: '#b45309', marginTop: 2 }}>{STATUS_NOTE[s.status]}</div>
                    )}
                    {s.from_old_key && !(id in (edits || {})) && (
                      <div role="note" style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                        Still read from its old key; saving here stores it in the new place.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {user.length > 0 && (
              <div style={{ marginTop: station.length ? 6 : 0 }}>
                <div style={{ fontSize: '0.78rem', color: '#475569', marginBottom: 4 }}>
                  Each user sets these for themselves from the station’s menu:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.8rem', color: '#334155' }}>
                  {user.map((s) => (
                    <li key={s.key}>
                      {s.label} <span style={{ color: '#64748b' }}>({s.type}{s.default != null ? `, default ${String(s.default)}` : ''})</span>
                      {s.help && <span style={{ color: '#64748b' }}> — {s.help}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </fieldset>
        );
      })}
    </div>
  );
}
